import {
  AggregationFunction,
  AttributeSelectorType,
  AttributeType,
  EqlAttributeConfigurationDc,
  StringSubType,
} from "@evergis/api";
import { IconButton, IconToggle } from "@evergis/ui";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fieldTypes } from "../../../../../evergis/core/attributes/fieldTypes";
import { useAttributesError } from "../../../../../hooks/useAttributesError";
import { AttributeError, validateAttributeName } from "../../../../../utils/validateAttributeName";
import { useValueChanged } from "../../hooks/useValueChanged";
import { AliasInput, NameInput, QueryAttributeNewContainer, StyledDropdown } from "./styled";

export interface QueryAttributeNewProps {
  attributeNames: string[];
  onClose: VoidFunction;
  onApply: (attributeConfig: EqlAttributeConfigurationDc) => void;
}

const typesOptions = fieldTypes.filter(
  value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType),
);

export const QueryAttributeNew: FC<QueryAttributeNewProps> = ({ attributeNames, onClose, onApply }) => {
  const { t } = useTranslation("dialogs");

  const [attributeName, setAttributeName] = useState("");
  const [alias, setAlias] = useState("");
  const [type, setType] = useState<AttributeType | StringSubType>(AttributeType.String);

  const [error, setError] = useState<AttributeError>();

  const { help, error: attributesError } = useAttributesError(error);

  const isNameChanged = useValueChanged(attributeName);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attributeNamesSet = useMemo(() => new Set<string>(attributeNames), [attributeNames.length]);

  const onCreate = useCallback(() => {
    let typeFormatted;

    if (type === StringSubType.Image) {
      typeFormatted = {
        type: AttributeType.String,
        subType: StringSubType.Image,
      };
    } else if (type === StringSubType.Attachments) {
      typeFormatted = {
        type: AttributeType.Json,
        subType: StringSubType.Attachments,
      };
    } else {
      typeFormatted = {
        type,
      };
    }

    onApply({
      ...typeFormatted,
      attributeName,
      alias,
      columnName: attributeName,
      isDisplayed: true,
      attributeSelectorType: AttributeSelectorType.None,
      isEditable: true,
      aggregation: AggregationFunction.None,
    });
  }, [onApply, type, attributeName, alias]);

  useEffect(() => {
    setError(validateAttributeName(attributeName, attributeNamesSet));
  }, [attributeName]);

  return (
    <QueryAttributeNewContainer>
      <IconToggle style={{ marginRight: "1rem" }} isSelected={false} kind={"password_show"} disabled />
      <StyledDropdown
        value={type}
        width={"3.5rem"}
        options={typesOptions.map(value => ({
          ...value,
          text: "",
        }))}
        onChange={val => setType(val[0].value as AttributeType)}
        zIndex={120}
      />
      <NameInput
        style={{ marginRight: "0.5rem" }}
        width={"21rem"}
        value={attributeName}
        onChange={event => setAttributeName(event.target.value)}
        placeholder={t("newLayer.attributeNameLat", { ns: "dialogs" })}
        label={t("newLayer.attributeName", { ns: "dialogs" })}
        help={isNameChanged && error ? attributesError : help}
        error={isNameChanged && !!error}
      />
      <AliasInput
        style={{ marginRight: "0.5rem" }}
        width={"100%"}
        value={alias}
        onChange={event => setAlias(event.target.value)}
        placeholder={t("alias", { ns: "style" })}
      />
      <IconButton kind={"close"} error onClick={onClose} />
      <IconButton kind={"success"} disabled={!!error} primary onClick={onCreate} />
    </QueryAttributeNewContainer>
  );
};
